import React, { useState } from 'react'
import { ChevronDownIcon, ChevronRightIcon, CalendarIcon, BarChart3Icon, UserIcon } from 'lucide-react'
import { parseStandardizedDate, formatStandardizedDate, getEstimatedDate, StandardizedDate } from '../utils/dateUtils'

interface AssessmentData {
  name: string
  type?: string
  status?: string
  date_administered?: string | {
    original_text: string
    estimated_date: string
  }
  grade?: string
  administrator?: string
  scores?: {
    raw_score?: string
    standard_score?: string
    percentile?: string
    grade_equivalent?: string
    other_scores?: string
  }
  results_summary?: string
  recommendations?: string
  used_for?: string
  selection_evidence?: string
  source_page?: number
}

interface AssessmentRendererProps {
  assessments: AssessmentData[]
  onSourceClick?: (pageNumber: number) => void
}

const AssessmentRenderer: React.FC<AssessmentRendererProps> = ({
  assessments,
  onSourceClick
}) => {
  const [expandedAssessments, setExpandedAssessments] = useState<Set<number>>(new Set())

  const toggleAssessment = (index: number) => {
    const newExpanded = new Set(expandedAssessments)
    if (newExpanded.has(index)) {
      newExpanded.delete(index)
    } else {
      newExpanded.add(index)
    }
    setExpandedAssessments(newExpanded)
  }

  const getStandardizedDate = (dateField?: AssessmentData['date_administered']): StandardizedDate | null => {
    if (!dateField) return null
    
    // Handle new standardized format from backend
    if (typeof dateField === 'object' && dateField.original_text) {
      return {
        originalText: dateField.original_text,
        estimatedDate: dateField.estimated_date,
        confidence: 'medium', // Backend provides reasonable estimates
        type: 'specific' // Backend has already processed it
      }
    }
    
    // Handle legacy string format
    if (typeof dateField === 'string') {
      return parseStandardizedDate(dateField)
    }
    
    return null
  }

  const formatAssessmentDate = (dateField?: AssessmentData['date_administered']): string => {
    const standardizedDate = getStandardizedDate(dateField)
    if (!standardizedDate) return 'Date not specified'
    
    // Always show the original text as it appeared in the document
    return formatStandardizedDate(standardizedDate)
  }

  const getDateForSorting = (dateField?: AssessmentData['date_administered']): Date | null => {
    const standardizedDate = getStandardizedDate(dateField)
    return getEstimatedDate(standardizedDate)
  }

  const getStatusColor = (status?: string): string => {
    if (!status) return 'bg-gray-100 text-gray-700'
    
    const lowerStatus = status.toLowerCase()
    if (lowerStatus.includes('completed')) return 'bg-green-100 text-green-800'
    if (lowerStatus.includes('needed') || lowerStatus.includes('required')) return 'bg-yellow-100 text-yellow-800'
    if (lowerStatus.includes('recommended')) return 'bg-blue-100 text-blue-800'
    if (lowerStatus.includes('progress') || lowerStatus.includes('ongoing')) return 'bg-purple-100 text-purple-800'
    
    return 'bg-gray-100 text-gray-700'
  }

  const getScoreSummary = (scores?: AssessmentData['scores']): string => {
    if (!scores) return ''
    
    const parts: string[] = []
    if (scores.standard_score) parts.push(`SS: ${scores.standard_score}`)
    if (scores.percentile) parts.push(`${scores.percentile}%ile`)
    if (scores.grade_equivalent) parts.push(`GE: ${scores.grade_equivalent}`)
    
    return parts.join(' • ')
  }

  const renderSourceReference = (pageNumber: number) => {
    if (!onSourceClick) return null
    return (
      <button
        onClick={(e) => {
          e.stopPropagation()
          onSourceClick(pageNumber)
        }}
        className="ml-2 p-1 rounded hover:bg-gray-100 transition-colors text-gray-400 hover:text-blue-600"
        title={`Go to page ${pageNumber} in PDF`}
      >
        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
        </svg>
      </button>
    )
  }

  // Group assessments by name to show progression over time
  const groupedAssessments = assessments.reduce((groups, assessment) => {
    const key = assessment.name.toLowerCase().trim()
    if (!groups[key]) {
      groups[key] = {
        name: assessment.name,
        type: assessment.type,
        entries: []
      }
    }
    groups[key].entries.push(assessment)
    return groups
  }, {} as Record<string, { name: string, type?: string, entries: AssessmentData[] }>)

  // Sort entries within each group by date, most recent first
  Object.values(groupedAssessments).forEach(group => {
    group.entries.sort((a, b) => {
      const dateA = getDateForSorting(a.date_administered)
      const dateB = getDateForSorting(b.date_administered)
      
      // Put assessments with dates first
      if (dateA && !dateB) return -1
      if (!dateA && dateB) return 1
      if (!dateA && !dateB) return 0
      
      // Sort by date, most recent first (both dates are guaranteed to exist here)
      return dateB!.getTime() - dateA!.getTime()
    })
  })

  // Sort groups by the most recent date in each group
  const sortedGroups = Object.values(groupedAssessments).sort((a, b) => {
    const mostRecentA = getDateForSorting(a.entries[0]?.date_administered)
    const mostRecentB = getDateForSorting(b.entries[0]?.date_administered)
    
    if (mostRecentA && !mostRecentB) return -1
    if (!mostRecentA && mostRecentB) return 1
    if (!mostRecentA && !mostRecentB) return 0
    
    // Both dates are guaranteed to exist here
    return mostRecentB!.getTime() - mostRecentA!.getTime()
  })

  if (sortedGroups.length === 0) {
    return (
      <div className="text-gray-500 italic p-4 bg-gray-50 rounded-lg">
        No assessments found
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {sortedGroups.map((group, groupIndex) => {
        const isExpanded = expandedAssessments.has(groupIndex)
        const mostRecentEntry = group.entries[0]
        const scoreSummary = getScoreSummary(mostRecentEntry.scores)
        
        return (
          <div key={groupIndex} className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
            {/* Header - Always Visible */}
            <div 
              className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => toggleAssessment(groupIndex)}
            >
              <div className="flex-1 min-w-0">
                {/* Assessment Name and Type */}
                <div className="flex items-center mb-2">
                  <h3 className="text-lg font-semibold text-gray-900 mr-3">
                    {group.name}
                  </h3>
                  {group.entries.length > 1 && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {group.entries.length} administrations
                    </span>
                  )}
                  {mostRecentEntry.status && (
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ml-2 ${getStatusColor(mostRecentEntry.status)}`}>
                      {mostRecentEntry.status}
                    </span>
                  )}
                  {mostRecentEntry.source_page && renderSourceReference(mostRecentEntry.source_page)}
                </div>
                
                {/* Date and Quick Summary */}
                <div className="flex items-center text-sm text-gray-600 space-x-4">
                  <div className="flex items-center">
                    <CalendarIcon className="h-4 w-4 mr-1" />
                    {group.entries.length === 1 
                      ? formatAssessmentDate(mostRecentEntry.date_administered)
                      : `${formatAssessmentDate(mostRecentEntry.date_administered)} (most recent of ${group.entries.length})`
                    }
                  </div>
                  {group.type && (
                    <div className="flex items-center">
                      <BarChart3Icon className="h-4 w-4 mr-1" />
                      {group.type}
                    </div>
                  )}
                  {mostRecentEntry.grade && (
                    <div className="flex items-center">
                      <UserIcon className="h-4 w-4 mr-1" />
                      Grade: {mostRecentEntry.grade}
                    </div>
                  )}
                  {scoreSummary && (
                    <div className="text-blue-600 font-medium">
                      {scoreSummary}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex-shrink-0 ml-4">
                {isExpanded ? (
                  <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                )}
              </div>
            </div>
            
            {/* Expanded Details */}
            {isExpanded && (
              <div className="border-t border-gray-100 px-4 pb-4">
                <div className="pt-4 space-y-6">
                  
                  {/* Assessment Timeline */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-4 flex items-center">
                      <CalendarIcon className="h-4 w-4 mr-2" />
                      Assessment History {group.entries.length > 1 && `(${group.entries.length} administrations)`}
                    </h4>
                    
                    <div className="space-y-4">
                      {group.entries.map((entry, entryIndex) => (
                        <div key={entryIndex} className="bg-gray-50 rounded-lg p-4">
                          {/* Entry Header */}
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center space-x-3">
                              <div className="font-medium text-gray-900">
                                {formatAssessmentDate(entry.date_administered)}
                              </div>
                              {entry.status && (
                                <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getStatusColor(entry.status)}`}>
                                  {entry.status}
                                </span>
                              )}
                              {entry.source_page && (
                                <div className="text-xs text-gray-500">
                                  Page {entry.source_page}
                                </div>
                              )}
                            </div>
                            {entry.used_for && (
                              <div className="text-xs text-gray-600 font-medium">
                                {entry.used_for}
                              </div>
                            )}
                          </div>
                          
                          {/* Entry Content */}
                          <div className="grid md:grid-cols-2 gap-4">
                            {/* Scores */}
                            {entry.scores && Object.values(entry.scores).some(score => score) && (
                              <div>
                                <h5 className="text-xs font-medium text-gray-600 mb-2">Scores</h5>
                                <div className="space-y-1 text-sm">
                                  {entry.scores.standard_score && (
                                    <div><span className="font-medium">Standard Score:</span> {entry.scores.standard_score}</div>
                                  )}
                                  {entry.scores.percentile && (
                                    <div><span className="font-medium">Percentile:</span> {entry.scores.percentile}</div>
                                  )}
                                  {entry.scores.raw_score && (
                                    <div><span className="font-medium">Raw Score:</span> {entry.scores.raw_score}</div>
                                  )}
                                  {entry.scores.grade_equivalent && (
                                    <div><span className="font-medium">Grade Equivalent:</span> {entry.scores.grade_equivalent}</div>
                                  )}
                                  {entry.scores.other_scores && (
                                    <div><span className="font-medium">Other Scores:</span> {entry.scores.other_scores}</div>
                                  )}
                                </div>
                              </div>
                            )}
                            
                            {/* Administrator & Details */}
                            <div>
                              {entry.administrator && (
                                <div className="mb-2">
                                  <h5 className="text-xs font-medium text-gray-600">Administrator</h5>
                                  <div className="text-sm text-gray-900">{entry.administrator}</div>
                                </div>
                              )}
                              {entry.selection_evidence && process.env.NODE_ENV === 'development' && (
                                <div className="mb-2">
                                  <h5 className="text-xs font-medium text-yellow-700">Selection Evidence (Dev)</h5>
                                  <div className="text-xs text-yellow-600">{entry.selection_evidence}</div>
                                </div>
                              )}
                              {entry.grade && (
                                <div className="mb-2">
                                  <h5 className="text-xs font-medium text-gray-600">Grade at Administration</h5>
                                  <div className="text-sm text-gray-900">{entry.grade}</div>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* Results Summary */}
                          {entry.results_summary && (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <h5 className="text-xs font-medium text-gray-600 mb-1">Results Summary</h5>
                              <p className="text-sm text-gray-900">{entry.results_summary}</p>
                            </div>
                          )}
                          
                          {/* Recommendations */}
                          {entry.recommendations && (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <h5 className="text-xs font-medium text-gray-600 mb-1">Recommendations</h5>
                              <p className="text-sm text-gray-900">{entry.recommendations}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default AssessmentRenderer
